import React, { useEffect, useState } from "react";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import SolutionImportCheckFields from "./SolutionImportCheckFields";
import ContactField from "../Contacts/ContactField";
import {
  useDeploySolutionMutation,
  useGetSolutionForDeploymentByUuidQuery,
  useUpdateApiConnectionMutation,
} from "../store/slices/settingsSlice";
import {
  ButtonGroup,
  Form,
  InputGroup,
  Modal,
  Tab,
  ToggleButton,
} from "react-bootstrap";
import { makeid, shortUUID } from "../helpers";
import {
  useCreateContactSetMutation,
  useGetContactSetsQuery,
  useLazyFindMatchingContactFieldsQuery,
  useUpsertFieldMutation,
} from "../store/slices/contactsSlice";
import {
  useCreateReportingTemplateMutation,
  useCreateReportingTopicMutation,
  useGetMatchingReportingTemplatesQuery,
  useGetReportingTopicsQuery,
  useLazyGetMatchingReportingTemplatesQuery,
} from "../store/slices/reportingSlice";
import ProjectStartForm from "../Builder/Components/sidebar/ProjectStartForm";
import { solutionRolesMapping } from "../Library/SelectSolutionProject";
import MonitoringConfigure from "../Monitoring/MonitoringConfigure";
import MonitoringDetails from "../Monitoring/MonitoringDetails";
import WeatherAlertConfiguration from "../Monitoring/WeatherAlertConfiguration";
import WeatherAlertCondition from "../Monitoring/WeatherAlertCondition";
import SelectApiConnection from "../Monitoring/SelectApiConnection";
import InboundSelector from "../Inbounds/InboundSelector";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  isEmptyOrUnmapped,
  hasEmptyNewFieldName,
  FIELD_TYPE_MAPPINGS,
} from "./utils/solutionImportHelpers";
import { handleCheckMatchingFields } from "./services/fieldMatchingService";
import { handleCreateFields } from "./services/fieldCreationService";
import {
  prepareSolutionDeployData,
  initializeWeatherMonitor,
  initializePingMonitor,
  initializeInboundCampaign,
} from "./services/deploymentService";
import { parseSolutionData } from "./services/jsonParsingService";
// TODO: When the user wants to manage a field manually and wants to select an existing field from the system, we need to check if a field with that name already exists and map it accordingly.
export default function SolutionImportModal({
  solutionClientUuid,
  setShow,
}: {
  solutionClientUuid: string;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const solutionDataRes = useGetSolutionForDeploymentByUuidQuery({
    uuid: solutionClientUuid,
  });
  const solutionData = solutionDataRes?.data?.LatestSource.Solution;
  const [mapping, setMapping] = useState<any>({});
  const [importedJSON, setImportedJSON] = useState<any>({
    Bag: { Internal: {}, External: {} },
    Item: {},
  });

  const [deploy] = useDeploySolutionMutation();
  const [upsertField] = useUpsertFieldMutation();
  const [createReportingTemplate] = useCreateReportingTemplateMutation();
  const [createReportingTopic] = useCreateReportingTopicMutation();
  const [createContactSet] = useCreateContactSetMutation();
  const [updateApiConnection] = useUpdateApiConnectionMutation();
  const [monitors, setMonitors] = useState<any>({});
  const [inboundAPIs, setInboundAPIs] = useState<any>({});
  const [inboundCampaigns, setInboundCampaigns] = useState<any>({});
  const [findMatchingContactFields] = useLazyFindMatchingContactFieldsQuery();
  const [findMatchingTemplates] = useLazyGetMatchingReportingTemplatesQuery();
  const [manualFields, setManualFields] = useState<string[]>([]);
  const [automaticallySelectedFields, setAutomaticallySelectedFields] =
    useState<string[]>([]);
  const [isDeployDisabled, setIsDeployDisabled] = useState(true);

  const { data: reportingTopicsData } = useGetReportingTopicsQuery({
    pagination: { page: 0, perPage: 100 },
  });
  const { data: contactSetsData } = useGetContactSetsQuery({
    page: 0,
    perPage: 100,
  });

  useEffect(() => {
    if (!solutionData?.Components || !contactSetsData || !reportingTopicsData)
      return;
    (async () => {
      // Parse solution data and build mapping
      const { mapping: m, importedJSON: newImportedJSON } =
        parseSolutionData(solutionData);
      setMapping(m);

      // Check for matching fields in the system
      const newExternal = await handleCheckMatchingFields(
        newImportedJSON,
        {
          findMatchingContactFields,
          findMatchingTemplates,
          contactSetsData,
          reportingTopicsData,
        },
        setDeployProgress,
        setAutomaticallySelectedFields
      );

      setImportedJSON({
        ...newImportedJSON,
        Bag: {
          ...newImportedJSON.Bag,
          External: { ...newImportedJSON.Bag.External, ...newExternal },
        },
      });

      // Initialize monitors and inbound campaigns
      solutionData.Components?.forEach((comp: any) => {
        if (comp.Role === "weather-monitor") {
          setMonitors((prev: any) => ({
            ...prev,
            ...initializeWeatherMonitor(comp.ComponentUuid),
          }));
        }
        if (comp.Role === "ping-monitor") {
          setMonitors((prev: any) => ({
            ...prev,
            ...initializePingMonitor(comp.ComponentUuid),
          }));
        }
        if (comp.Role === "inbound-message") {
          setInboundCampaigns((prev: any) => ({
            ...prev,
            ...initializeInboundCampaign(comp.ComponentUuid),
          }));
        }
      });
      setIsDeployDisabled(false);
    })();
  }, [solutionData, contactSetsData, reportingTopicsData]);

  const [showDeployModal, setShowDeployModal] = useState(false);
  const [defaultBaseUrl, setDefaultBaseUrl] = useState("");
  const [variableDefaults, setVariableDefaults] = useState({});
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployProgress, setDeployProgress] = useState("");

  const handleCreateFieldsWrapper = async () => {
    const updates = await handleCreateFields(
      importedJSON,
      manualFields,
      {
        upsertField,
        createContactSet,
        createReportingTemplate,
        createReportingTopic,
        updateApiConnection,
      },
      defaultBaseUrl,
      setDeployProgress
    );

    // Apply all updates at once
    setImportedJSON((prev: any) => {
      const updated = { ...prev };

      Object.entries(updates || {}).forEach(([fieldType, fieldUpdates]) => {
        Object.entries(fieldUpdates || {}).forEach(([key, value]) => {
          if (updated.Bag.External[fieldType]) {
            updated.Bag.External[fieldType][key] = value;
          }
        });
      });

      return updated;
    });

    // Wait a bit for state to update before deploying
    await new Promise((resolve) => setTimeout(resolve, 100));
  };

  const handleDeploy = async () => {
    setIsDeploying(true);
    setDeployProgress("Preparing deployment...");

    try {
      // Create any unmapped fields (excluding manually managed fields) before deploying
      await handleCreateFieldsWrapper();

      setDeployProgress("Deploying solution...");

      // Prepare deployment data using the service
      const solutionDeployData = prepareSolutionDeployData(
        solutionData,
        solutionClientUuid,
        variableDefaults,
        mapping,
        importedJSON,
        monitors,
        inboundAPIs,
        inboundCampaigns
      );

      await deploy(solutionDeployData);
      setDeployProgress("Deployment completed successfully!");

      // Reset after a brief delay to show success message
      setTimeout(() => {
        setIsDeploying(false);
        setDeployProgress("");
        setShowDeployModal(false);
      }, 2000);
    } catch (error) {
      setDeployProgress("Deployment failed. Please try again.");
      console.log(error);
      setTimeout(() => {
        setIsDeploying(false);
        setDeployProgress("");
      }, 3000);
    }
  };

  return (
    <div className="position-relative">
      {deployProgress && (
        <div className="position-absolute top-0 start-0 h-100 w-100 bg-white p-3 bg-opacity-50 d-flex justify-content-center align-items-center">
          <div className="alert alert-info mt-3 d-flex align-items-center">
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span>{deployProgress}</span>
          </div>
        </div>
      )}
      {importedJSON.Bag.External && (
        <SolutionImportCheckFields
          importedJSON={importedJSON}
          manualFields={manualFields}
          setManualFields={setManualFields}
          setImportedJSON={(f) => {
            setImportedJSON((prev: any) => {
              const newImportedJson = f instanceof Function ? f(prev) : f;
              return newImportedJson;
            });
          }}
          firstTab={[
            <Tab eventKey="review" title="Review & Deploy">
              <div className="p-3">
                <div className="row">
                  <div className="col-4">
                    {Object.entries(importedJSON.Bag.External || {}).flatMap(
                      ([fieldKey, fieldValue]: [string, any]) =>
                        Object.entries(fieldValue).filter(
                          ([key, value]: [string, any]) => key
                        )
                    ).length === 0 ? (
                      <div className="p-3 border rounded-3 bg-light">
                        There are no external fields to be created. You can
                        press "Confirm deploy" now.
                      </div>
                    ) : Object.entries(importedJSON.Bag.External || {}).flatMap(
                        ([fieldKey, fieldValue]: [string, any]) =>
                          Object.entries(fieldValue).filter(
                            ([key, value]: [string, any]) =>
                              !manualFields.includes(key) &&
                              isEmptyOrUnmapped(value)
                          )
                      ).length > 0 ? (
                      <div className="p-3 border rounded-3 bg-light">
                        In order for this solution to be deployed, the following
                        external fields need to be created. They will be created
                        automatically when you press "Confirm Deploy", or you
                        can fill them manually in the next tabs.
                      </div>
                    ) : (
                      <div className="p-3 border rounded-3 bg-light">
                        Everything is ready. You can review the changes in the
                        next tabs or you can press "Confirm deploy" now.
                      </div>
                    )}
                  </div>
                  <div className="col-8">
                    {Object.entries(
                      importedJSON.Bag.External.ContactField || {}
                    ).map(([fieldKey, fieldValue]: [string, any]) => {
                      const fieldLabel = importedJSON.Item.ContactFields.find(
                        (f: any) => f.uuid === fieldKey
                      )?.label;

                      return isEmptyOrUnmapped(fieldValue) ? (
                        <div key={fieldKey} className="text-danger">
                          Contact Field "{fieldLabel}" will be created.
                        </div>
                      ) : manualFields.includes(fieldKey) ? (
                        <div key={fieldKey} className="">
                          Contact Field "{fieldLabel}" is managed manually.
                        </div>
                      ) : automaticallySelectedFields.includes(fieldKey) ? (
                        <div key={fieldKey} className="text-success">
                          Contact Field "{fieldLabel}" is automatically mapped
                          to a system field.
                        </div>
                      ) : null;
                    })}
                    {Object.entries(
                      importedJSON.Bag.External.ContactSet || {}
                    ).map(([fieldKey, fieldValue]: [string, any]) => {
                      const fieldLabel = importedJSON.Item.ContactGroups.find(
                        (f: any) => f.uuid === fieldKey
                      )?.label;

                      return isEmptyOrUnmapped(fieldValue) ? (
                        <div key={fieldKey} className="text-danger">
                          Contact Group "{fieldLabel}" will be created.
                        </div>
                      ) : manualFields.includes(fieldKey) ? (
                        <div key={fieldKey} className="">
                          Contact Group "{fieldLabel}" is managed manually.
                        </div>
                      ) : automaticallySelectedFields.includes(fieldKey) ? (
                        <div key={fieldKey} className="text-success">
                          Contact Group "{fieldLabel}" is automatically mapped
                          to a system field.
                        </div>
                      ) : null;
                    })}
                    {Object.entries(
                      importedJSON.Bag.External.ReportingTemplate || {}
                    ).map(([fieldKey, fieldValue]: [string, any]) => {
                      const fieldLabel =
                        importedJSON.Item.ReportingTemplates.find(
                          (f: any) => f.uuid === fieldKey
                        )?.label;

                      return isEmptyOrUnmapped(fieldValue) ? (
                        <div key={fieldKey} className="text-danger">
                          Reporting Template "{fieldLabel}" will be created.
                        </div>
                      ) : manualFields.includes(fieldKey) ? (
                        <div key={fieldKey} className="">
                          Reporting Template "{fieldLabel}" is managed manually.
                        </div>
                      ) : automaticallySelectedFields.includes(fieldKey) ? (
                        <div key={fieldKey} className="text-success">
                          Reporting Template "{fieldLabel}" is automatically
                          mapped to a system field.
                        </div>
                      ) : null;
                    })}
                    {Object.entries(importedJSON.Bag.External.Topic || {}).map(
                      ([fieldKey, fieldValue]: [string, any]) => {
                        const fieldLabel =
                          importedJSON.Item.ReportingTopics.find(
                            (f: any) => f.uuid === fieldKey
                          )?.label;

                        return isEmptyOrUnmapped(fieldValue) ? (
                          <div key={fieldKey} className="text-danger">
                            Reporting Topic "{fieldLabel}" will be created.
                          </div>
                        ) : manualFields.includes(fieldKey) ? (
                          <div key={fieldKey} className="">
                            Reporting Topic "{fieldLabel}" is managed manually.
                          </div>
                        ) : automaticallySelectedFields.includes(fieldKey) ? (
                          <div key={fieldKey} className="text-success">
                            Reporting Topic "{fieldLabel}" is automatically
                            mapped to a system field.
                          </div>
                        ) : null;
                      }
                    )}
                    {Object.entries(
                      importedJSON.Bag.External.ApiConnection || {}
                    ).map(([fieldKey, fieldValue]: [string, any]) =>
                      !manualFields.includes(fieldKey) &&
                      isEmptyOrUnmapped(fieldValue) ? (
                        <div key={fieldKey}>
                          <span className="text-danger">
                            Api Connection needs to be created.
                          </span>

                          <div className="my-3">
                            You need to enter the Default Base URL for new API
                            Connections
                            <Form.Control
                              type="text"
                              value={defaultBaseUrl}
                              onChange={(e) =>
                                setDefaultBaseUrl(e.target.value)
                              }
                            />
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              </div>
            </Tab>,
          ]}
          end={solutionData?.Components?.filter(
            (comp: any) => comp.Type === "project"
          ).map((comp: any) => (
            <Tab
              tabClassName="bg-purple-gradient-darker"
              eventKey={comp.ComponentUuid}
              title={`${
                (solutionRolesMapping as any)[comp.Role]?.label || comp.Role
              }: ${comp.Item?.Label || comp.ComponentUuid.substring(0, 8)}`}
              key={comp.ComponentUuid}
            >
              <div className="p-3">
                {monitors[comp.ComponentUuid] && (
                  <div className="mb-4">
                    <h4>
                      Configure Monitor Settings for{" "}
                      {comp.Item?.Label || comp.ComponentUuid.substring(0, 8)}
                    </h4>
                    <div className="alert alert-light">
                      These monitors require setup, but they can be updated
                      later. Set the status to draft to pause the monitoring,
                      otherwise the process will start now.
                    </div>
                    <div className="mb-3">
                      <div>Monitor Priority:</div>
                      <ButtonGroup>
                        <ToggleButton
                          value="low"
                          id="radio-priority-low"
                          className={`bg-success text-white border-0 ${
                            monitors[comp.ComponentUuid].Priority === "low"
                              ? "bg-opacity-100"
                              : "bg-opacity-25"
                          }`}
                          type="radio"
                          variant=""
                          name="radio-priority"
                          checked={
                            monitors[comp.ComponentUuid].Priority === "low"
                          }
                          onChange={() =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: {
                                ...monitors[comp.ComponentUuid],
                                Priority: "low",
                              },
                            })
                          }
                        >
                          Low
                        </ToggleButton>
                        <ToggleButton
                          value="medium"
                          id="radio-priority-medium"
                          type="radio"
                          variant=""
                          className={`bg-warning text-white border-0 ${
                            monitors[comp.ComponentUuid].Priority === "medium"
                              ? "bg-opacity-100"
                              : "bg-opacity-25"
                          }`}
                          name="radio-priority"
                          checked={
                            monitors[comp.ComponentUuid].Priority === "medium"
                          }
                          onChange={() =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: {
                                ...monitors[comp.ComponentUuid],
                                Priority: "medium",
                              },
                            })
                          }
                        >
                          Medium
                        </ToggleButton>
                        <ToggleButton
                          value="high"
                          id="radio-priority-high"
                          type="radio"
                          variant=""
                          className={`bg-danger text-white border-0 ${
                            monitors[comp.ComponentUuid].Priority === "high"
                              ? "bg-opacity-100"
                              : "bg-opacity-25"
                          }`}
                          name="radio-priority"
                          checked={
                            monitors[comp.ComponentUuid].Priority === "high"
                          }
                          onChange={() =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: {
                                ...monitors[comp.ComponentUuid],
                                Priority: "high",
                              },
                            })
                          }
                        >
                          High
                        </ToggleButton>
                      </ButtonGroup>
                    </div>
                    <div className="mb-3">
                      <div>Monitor Status:</div>
                      <Input
                        value={[monitors[comp.ComponentUuid].Status]}
                        onChange={(v: any) => {
                          setMonitors({
                            ...monitors,
                            [comp.ComponentUuid]: {
                              ...monitors[comp.ComponentUuid],
                              Status: v[0],
                            },
                          });
                        }}
                        inputType="radio"
                        radioName={`radio-monitor-status-${comp.ComponentUuid}`}
                        options={[
                          { value: "active", label: "ACTIVE" },
                          { value: "draft", label: "DRAFT" },
                          { value: "archived", label: "ARCHIVED" },
                        ]}
                      />
                    </div>

                    {monitors[comp.ComponentUuid].Kind === "ping" && (
                      <div className="mb-4">
                        <MonitoringConfigure
                          monitor={monitors[comp.ComponentUuid]}
                          setMonitor={(monitor: any) =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: monitor,
                            })
                          }
                          backendErrors={undefined}
                          setSelectedApiDetails={(api: any) =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: {
                                ...monitors[comp.ComponentUuid],
                                selectedApiDetails: api,
                              },
                            })
                          }
                          method={
                            monitors[comp.ComponentUuid].Method || "selectAnApi"
                          }
                          setMethod={(newMethod: any) => {
                            if (newMethod === "createANewApi") {
                              setMonitors({
                                ...monitors,
                                [comp.ComponentUuid]: {
                                  ...monitors[comp.ComponentUuid],
                                  Method: newMethod,
                                  ApiConnectionUuid: null,
                                  ApiConnection: {
                                    Status: "production",
                                    Label: comp.Label + " API Connection",
                                    Kind: "third-party",
                                    Profile: {
                                      BaseUrl: "http://api:9090",
                                      AuthHeader: "Authorization",
                                      AuthValue:
                                        "Basic <base64encryptedstring>",
                                      AuthKind: "",
                                      Timeout: 1,
                                      DefaultMethod: "GET",
                                      ContentType: "JSON",
                                      QueryParams: null,
                                      FormData: null,
                                      JsonPayload: null,
                                      JsonPayloadTokens: null,
                                      ComponentConfig: null,
                                      JsonApiDataCollections: null,
                                    },
                                  },
                                },
                              });
                            }
                            if (newMethod === "selectAnApi") {
                              setMonitors({
                                ...monitors,
                                [comp.ComponentUuid]: {
                                  ...monitors[comp.ComponentUuid],
                                  ApiConnectionUuid: null,
                                  ApiConnection: null,
                                  Method: newMethod,
                                },
                              });
                            }
                          }}
                        />
                        <MonitoringDetails
                          monitor={monitors[comp.ComponentUuid]}
                          setMonitor={(monitor: any) =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: monitor,
                            })
                          }
                          selectedApiDetails={
                            monitors[comp.ComponentUuid].selectedApiDetails
                          }
                        />
                      </div>
                    )}
                    {monitors[comp.ComponentUuid].Kind === "weather" && (
                      <div className="mb-4">
                        <WeatherAlertConfiguration
                          weatherAlertData={
                            monitors[comp.ComponentUuid].WeatherAlert
                          }
                          setWeatherAlertData={(WeatherAlert: any) =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: {
                                ...monitors[comp.ComponentUuid],
                                WeatherAlert,
                              },
                            })
                          }
                          errors={undefined}
                        />
                        <WeatherAlertCondition
                          weatherAlertData={
                            monitors[comp.ComponentUuid].WeatherAlert
                          }
                          setWeatherAlertData={(WeatherAlert: any) =>
                            setMonitors({
                              ...monitors,
                              [comp.ComponentUuid]: {
                                ...monitors[comp.ComponentUuid],
                                WeatherAlert,
                              },
                            })
                          }
                          errors={undefined}
                        />
                      </div>
                    )}
                  </div>
                )}

                {comp.Role === "inbound-api" && (
                  <div className="mb-4">
                    <h5>Select an API Connection</h5>
                    <div>
                      You can select an existing API connection or leave it
                      blank to create a new one.
                    </div>
                    {!inboundAPIs[comp.ComponentUuid] && (
                      <div className="alert alert-info">
                        You have not selected an API connection yet. A new one
                        will be created for you automatically.
                      </div>
                    )}
                    <SelectApiConnection
                      apiKind="logic"
                      selected={inboundAPIs[comp.ComponentUuid]}
                      onSelect={(api) => {
                        setInboundAPIs({
                          ...inboundAPIs,
                          [comp.ComponentUuid]: api?.Uuid,
                        });
                      }}
                    />
                  </div>
                )}

                {comp.Role === "inbound-message" && (
                  <div className="mb-4">
                    <h5>Select an Inbound channel and address</h5>
                    <InboundSelector
                      inboundCampaign={
                        inboundCampaigns[comp.ComponentUuid] || {}
                      }
                      setInboundCampaign={(inboundCampaign) => {
                        setInboundCampaigns({
                          ...inboundCampaigns,
                          [comp.ComponentUuid]: inboundCampaign,
                        });
                      }}
                    />
                    <Input
                      label="Keywords to match (Optional)"
                      value={
                        inboundCampaigns[comp.ComponentUuid]?.Keywords || []
                      }
                      onChange={(keywords: any[]) => {
                        setInboundCampaigns({
                          ...inboundCampaigns,
                          [comp.ComponentUuid]: {
                            ...inboundCampaigns[comp.ComponentUuid],
                            Keywords: keywords,
                          },
                        });
                      }}
                      isMulti
                      options={undefined}
                      isValidNewOption={(v) => !!v.trim()}
                    />
                  </div>
                )}

                <h4 className="">
                  <span className="text-muted"> Component: </span>{" "}
                  {comp.Item?.Label}
                </h4>
                <div className="text-secondary">{comp.Description}</div>
                <div className="text-secondary">{comp.Item?.Description}</div>
                <ProjectStartForm
                  projectID={undefined}
                  showHiddenVariables
                  filterInputArguments={(arg: any) =>
                    arg.IsParameter && !arg.SolutionDoNotConfigure
                  }
                  onChange={(varDef: any) => {
                    setVariableDefaults((prev) => ({
                      ...prev,
                      [comp.ComponentUuid]: varDef,
                    }));
                  }}
                  hideStartButton={true}
                  initialInputArguments={comp.Item.InputArguments}
                  initialVariableGroups={comp.Item.VariableGroups}
                />
              </div>
            </Tab>
          ))}
        />
      )}

      <div className="d-flex justify-content-end mt-3">
        <button
          className="btn btn-colored"
          onClick={() => setShow(false)}
          disabled={isDeploying}
        >
          Cancel
        </button>
        <button
          className="btn btn-colored"
          onClick={handleDeploy}
          disabled={
            isDeployDisabled ||
            isDeploying ||
            // Disable if any manually managed field is set to create new but has no name
            Object.entries(importedJSON.Bag.External || {}).flatMap(
              ([fieldKey, fieldValue]: [string, any]) =>
                Object.entries(fieldValue || {}).filter(
                  ([key, value]: [string, any]) =>
                    manualFields.includes(key) &&
                    (hasEmptyNewFieldName(value) || isEmptyOrUnmapped(value))
                )
            ).length > 0
          }
        >
          Confirm Deploy
        </button>
      </div>

      <div>
        <JsonCollapse
          json={{
            // mapping,
            importedJSON,
            // solution,
            monitors,
            inboundAPIs,
            inboundCampaigns,
            variableDefaults,
            // manualFields,
          }}
        />
      </div>
    </div>
  );
}
